import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ToastController } from '@ionic/angular';
import { BerService } from '../ber.service';
import { BER_OPCIOK } from '../ber.opciok';
import { naploNyomtat, NyomtatasOszlop } from '../../naplo/naplo-nyomtatas';
import { AdatszolgApi, csvLetolt, evLista, ftSzoveg, HONAPNEV } from './adatszolg.api';

/*  BÉR – Bér adatszolgáltatás (08) – RLB: Bér adatszolgáltatás → Bér adatszolgáltatás (xx08).
 *
 *  A havi 2608 bevallás adatai a tárolt számfejtésekből: természetes személyenként az M-lapok fő
 *  értékei (a 2608 nyomtatvány sorszámaival; a betöltött sablon verzióját a kiszolgáló adja) és a főlap összesítői. ELEMZÉS ÉS RÖGZÍTÉSI
 *  SEGÉDLET + CSV – nem beküldhető bevallás: a beküldhető 2608-at a NAV-nyomtatványkitöltő állítja
 *  elő, ugyanebből a végpontból (GET /api/ber-adatszolg/08/:ugyfelId, lásd ber-adatszolg.js).
 */
@Component({
  selector: 'app-ber-adatszolgaltatas-08',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './adatszolgaltatas-08.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../ber-shared.scss', './adatszolg.component.scss'],
})
export class Adatszolgaltatas08Component implements OnInit {
  evek = evLista();
  honapok = BER_OPCIOK.honapok;
  adat: any = null;
  kijelolt: any = null;
  tolt = false;

  constructor(private ber: BerService, private api: AdatszolgApi, private toast: ToastController) {}

  get aktiv() { return this.ber.aktiv; }

  async ngOnInit() { await this.ber.init(); await this.betolt(); }
  async evValt(ev: number) { await this.ber.setAktiv({ ev }); await this.betolt(); }
  async hoValt(ho: number) { await this.ber.setAktiv({ ho }); await this.betolt(); }

  get idoszakNev(): string { return `${this.aktiv.ev}. ${HONAPNEV[this.aktiv.ho - 1] || ''}`; }

  async betolt() {
    this.kijelolt = null;
    if (!this.aktiv.ugyfelId) return;
    this.tolt = true;
    try { this.adat = await this.api.get(`/08/${this.aktiv.ugyfelId}?ev=${this.aktiv.ev}&ho=${this.aktiv.ho}`); }
    catch (e: any) { this.adat = null; this.hiba(this.api.hibaUzenet(e, 'Nem sikerült betölteni a 08-as adatokat.')); }
    finally { this.tolt = false; }
  }

  valaszt(m: any) { this.kijelolt = this.kijelolt === m ? null : m; }

  get vanKiva(): boolean { return !!this.adat?.folap?.kiva; }

  /** A rács oszlopai (a képernyő, a nyomtatás és a CSV közös leírása). A `sor` a 2608-sablon sora. */
  get oszlopok(): (NyomtatasOszlop & { sor?: string })[] {
    const l: (NyomtatasOszlop & { sor?: string })[] = [
      { k: 'nev', c: 'Dolgozó', szeles: true },
      { k: 'adoazonosito', c: 'Adóazonosító' },
      { k: 'jogviszony_kod', c: 'Kód', sor: 'M-08' },
      { k: 'napok', c: 'Bizt. nap', num: true },
      { k: 'brutto', c: 'Bruttó', num: true },
      { k: 'osszevont_jovedelem', c: 'Összevont jöv. (288.d)', num: true, sor: '288' },
      { k: 'kedv', c: 'Kedvezmények (323)', num: true, sor: '323', cs: 'cs-afa' },
      { k: 'szja_alap', c: 'SZJA-alap (324)', num: true, sor: '324', cs: 'cs-afa' },
      { k: 'szja_levont', c: 'SZJA (331)', num: true, sor: '331', cs: 'cs-afa' },
      { k: 'tb_alap', c: 'TB-alap (626)', num: true, sor: '626', cs: 'cs-penz' },
      { k: 'tb', c: 'TB 18,5% (629)', num: true, sor: '629', cs: 'cs-penz' },
      { k: 'csaladi_jarulekkedvezmeny', c: 'Csal. j.kedv. (630)', num: true, sor: '630', cs: 'cs-penz' },
      { k: 'tb_fizetendo', c: 'TB fizetendő (633)', num: true, sor: '633', cs: 'cs-penz' },
      { k: 'szocho_alap', c: 'Szocho-alap (385)', num: true, sor: '385' },
      { k: 'szocho', c: 'Szocho (386)', num: true, sor: '386' },
    ];
    if (this.vanKiva) l.push({ k: 'kiva_alap', c: 'KIVA-alap (tájék.)', num: true }, { k: 'kiva', c: 'KIVA (tájék.)', num: true });
    return l;
  }

  ertek(m: any, o: NyomtatasOszlop): any { return o.k === 'kedv' ? m.kedvezmenyek?.osszesen : m[o.k]; }
  szoveg(m: any, o: NyomtatasOszlop): string {
    const v = this.ertek(m, o);
    if (o.k === 'napok') return v == null ? '' : String(v);
    return o.num ? ftSzoveg(v) : String(v ?? '');
  }
  osszeg(o: NyomtatasOszlop): number {
    return (this.adat?.mlapok || []).reduce((x: number, m: any) => x + (Number(this.ertek(m, o)) || 0), 0);
  }

  /** A kijelölt dolgozó M-04 és M-05 sorai megnevezéssel (a részletek panelhez). */
  kedvSorok(m: any): { sor: string; c: string; v: number }[] {
    const k = m.kedvezmenyek || {};
    return [
      { sor: '311.b', c: '30 év alatti anyák', v: k.anya30 }, { sor: '312.b', c: 'Két gyermeket nevelő anyák', v: k.anya2 },
      { sor: '313.b', c: 'Három gyermeket nevelő anyák', v: k.anya3 }, { sor: '314.b', c: 'Négy vagy több gyermeket nevelő anyák', v: k.anya4 },
      { sor: '319.b', c: '25 év alatti fiatalok', v: k.fiatal25 }, { sor: '320.b', c: 'Személyi kedvezmény', v: k.szemelyi },
      { sor: '321.a', c: 'Első házasok (nyilatkozat szerint)', v: k.elsohazas_nyilatkozat }, { sor: '321.b', c: 'Első házasok (érvényesített)', v: k.elsohazas },
      { sor: '322.a', c: 'Családi (nyilatkozat szerint)', v: k.csaladi_nyilatkozat }, { sor: '322.b', c: 'Családi (érvényesített)', v: k.csaladi },
      { sor: '—', c: 'Jogcímre nem bontható', v: k.nem_bonthato },
    ].filter((x) => Number(x.v) > 0);
  }
  jovSorok(m: any): { sor: string; v: number }[] {
    return Object.entries(m.jovedelmek || {}).map(([sor, v]) => ({ sor, v: Number(v) })).sort((a, b) => Number(a.sor) - Number(b.sor));
  }
  sorKodok(m: any): [string, number][] { return Object.entries(m.sorok || {}) as [string, number][]; }

  nyomtat() {
    if (!this.adat) return;
    const u = this.adat.adozo || {}; const f = this.adat.folap || {};
    const oszl = this.oszlopok;
    naploNyomtat({
      cim: 'Bér adatszolgáltatás (08) – elemzés',
      ugyfel: { nev: u.nev, adoszam: u.adoszam, cim: u.cim },
      ev: this.aktiv.ev,
      mod: 'Nem beküldhető bevallás',
      szurok: [['Időszak', this.idoszakNev], ['70. sor (SZJA)', `${ftSzoveg(f.szja_levont)} Ft`], ['103. sor (szocho)', `${ftSzoveg(f.szocho)} Ft`],
        ['160. sor (TB-járulék)', `${ftSzoveg(f.tb_jarulek)} Ft`], ['Érintett személyek', String(f.termeszetes_szemelyek_szama)]],
      oszlopok: oszl,
      sorok: this.adat.mlapok,
      ertek: (m, o) => this.szoveg(m, o),
      osszegSor: (o) => (o.num && o.k !== 'napok' ? ftSzoveg(this.osszeg(o)) : ''),
      uresOszlopNelkul: false,
    });
  }

  csv() {
    if (!this.adat) return;
    const fej = ['Adóazonosító', 'Név', 'Foglalkoztatás minősége kód', 'Biztosítási napok', 'Bruttó', 'SZJA-mentes jogcím',
      'M04 270.d', 'M04 274.d', 'M04 276.d', 'M04 284.d', 'M04 288.d', 'M04 270.e', 'M04 270.f',
      'M05 311.b', 'M05 312.b', 'M05 313.b', 'M05 314.b', 'M05 319.b', 'M05 320.b', 'M05 321.a', 'M05 321.b', 'M05 322.a', 'M05 322.b',
      'M05 323.b', 'M05 324.b', 'M05 325.b', 'M05 330.b', 'M05 331.b',
      'M09-02 626.c', 'M09-02 629.c', 'M09-02 620.c', 'M09-02 630.c', 'M09-02 633.c', 'M07 385.c', 'M07 386.c',
      'KIVA-alap (tájék.)', 'KIVA (tájék.)', 'Hiányzó törzsadat'];
    const sorok = this.adat.mlapok.map((m: any) => {
      const k = m.kedvezmenyek; const j = m.jovedelmek || {};
      return [m.adoazonosito || '', m.nev, m.jogviszony_kod || '', m.napok, m.brutto, m.nem_szja,
        j['270'] || 0, j['274'] || 0, j['276'] || 0, j['284'] || 0, m.osszevont_jovedelem, m.kedv_alap_anyak, m.kedv_alap_fiatal,
        k.anya30, k.anya2, k.anya3, k.anya4, k.fiatal25, k.szemelyi, k.elsohazas_nyilatkozat, k.elsohazas, k.csaladi_nyilatkozat, k.csaladi,
        k.osszesen, m.szja_alap, m.berjovedelem, m.szja, m.szja_levont,
        m.tb_alap, m.tb, m.csaladi_jarulekkedvezmeny_alap, m.csaladi_jarulekkedvezmeny, m.tb_fizetendo, m.szocho_alap, m.szocho,
        m.kiva_alap, m.kiva, (m.hianyok || []).join(', ')];
    });
    const f = this.adat.folap;
    sorok.push([]);
    sorok.push(['FŐLAP', `70.c levont SZJA: ${f.szja_levont}`, `74.d: ${f.szja_kotelezettseg_ezer} eFt`, `103.c szocho: ${f.szocho}`,
      `109.d: ${f.szocho_kotelezettseg_ezer} eFt`, `160.c TB-járulék: ${f.tb_jarulek}`, `160.d: ${f.tb_jarulek_ezer} eFt`,
      `személyek: ${f.termeszetes_szemelyek_szama}`, `nyugdíjas: ${f.nyugdijasok_szama}`]);
    const hh = String(this.aktiv.ho).padStart(2, '0');
    csvLetolt(`08_adatszolgaltatas_${this.aktiv.ev}${hh}.csv`, fej, sorok);
  }

  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 3000, color: 'danger' })).present(); }
}
